import { useState } from 'react';
import { UserPlus, Users } from 'lucide-react';
import { Segments, ToolbarGrow, ToolbarSearch } from '../ui/ListToolbar';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { CollaboratorRow } from './CollaboratorRow';
import { InviteCollaboratorModal } from './InviteCollaboratorModal';
import { CollaboratorEditModal } from './CollaboratorEditModal';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useCollaboratorActivity } from '../../hooks/queries/useCollaboratorActivity';
import { useAuthStore } from '../../store/useAuthStore';
import type { CollaboratorWithCategories, CollaboratorRole } from '../../services/collaborators/collaboratorService';

type StatusFilter = 'all' | 'active' | 'inactive';
type RoleFilter = 'all' | CollaboratorRole;

export function TeamTab() {
  const { data: collaborators = [], isLoading, error } = useCollaborators();
  const isAdmin = useAuthStore((s) => s.collaborator?.role === 'admin');
  // Temps de connexion : visible admins uniquement (la RPC est elle-même admin-only).
  const { data: activity = {} } = useCollaboratorActivity(isAdmin);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [editingCollaborator, setEditingCollaborator] = useState<CollaboratorWithCategories | null>(null);

  const filtered = collaborators.filter((c) => {
    const matchesSearch =
      !search ||
      c.full_name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && c.is_active) ||
      (statusFilter === 'inactive' && !c.is_active);

    const matchesRole = roleFilter === 'all' || c.role === roleFilter;

    return matchesSearch && matchesStatus && matchesRole;
  });

  const statusBadges: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'Tous' },
    { key: 'active', label: 'Actifs' },
    { key: 'inactive', label: 'Inactifs' },
  ];

  const roleBadges: { key: RoleFilter; label: string }[] = [
    { key: 'all', label: 'Tous les rôles' },
    { key: 'admin', label: 'Admin' },
    { key: 'lawyer', label: 'Avocat' },
    { key: 'secretary', label: 'Assistant(e)' },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2
            className="text-xl font-bold text-cime-primary"
          >
            Équipe du cabinet
          </h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            {collaborators.length} collaborateur{collaborators.length > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <ToolbarSearch value={search} onChange={setSearch} placeholder="Rechercher par nom ou email..." className="w-[300px]" />

        <Segments
          label="Statut"
          value={statusFilter}
          onChange={setStatusFilter}
          options={statusBadges.map(({ key, label }) => ({ value: key, label }))}
        />
        <Segments
          label="Rôle"
          value={roleFilter}
          onChange={setRoleFilter}
          options={roleBadges.map(({ key, label }) => ({ value: key, label }))}
        />
        <ToolbarGrow />
        <Button
          variant="primary"
          onClick={() => setIsInviteOpen(true)}
          className="shrink-0"
        >
          <UserPlus className="w-4 h-4" aria-hidden />
          Inviter un collaborateur
        </Button>
      </div>

      {/* Table */}
      <div className="bg-cime-surface rounded-cime border border-neutral-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-600 text-sm">
            Erreur lors du chargement de l'équipe. Veuillez réessayer.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="cime-table text-[13px]">
              <thead>
                <tr>
                  <th className="px-2.5 py-2 text-left first:pl-[18px] last:pr-[18px]">
                    Collaborateur
                  </th>
                  <th className="px-2.5 py-2 text-left first:pl-[18px] last:pr-[18px]">
                    Rôle
                  </th>
                  <th className="px-2.5 py-2 text-left first:pl-[18px] last:pr-[18px]">
                    Compétences
                  </th>
                  {isAdmin && (
                    <th className="px-2.5 py-2 text-left first:pl-[18px] last:pr-[18px]">
                      Aujourd'hui
                    </th>
                  )}
                  <th className="px-2.5 py-2 text-left first:pl-[18px] last:pr-[18px]">
                    Statut
                  </th>
                  {isAdmin && (
                    <th className="px-2.5 py-2 text-left first:pl-[18px] last:pr-[18px]">
                      Dernière connexion
                    </th>
                  )}
                  <th className="px-2.5 py-2 text-left first:pl-[18px] last:pr-[18px]">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((collaborator, index) => (
                    <CollaboratorRow
                      key={collaborator.id}
                      collaborator={collaborator}
                      onEdit={setEditingCollaborator}
                      index={index}
                      showActivity={isAdmin}
                      activity={activity[collaborator.id] ?? null}
                    />
                  ))
                ) : (
                  <tr>
                    <td colSpan={isAdmin ? 8 : 6} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-2 text-neutral-400">
                        <Users className="w-10 h-10" aria-hidden />
                        <p className="text-sm font-medium">
                          {search || statusFilter !== 'all' || roleFilter !== 'all'
                            ? 'Aucun collaborateur ne correspond aux filtres.'
                            : 'Aucun collaborateur. Commencez par inviter votre équipe.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <InviteCollaboratorModal
        open={isInviteOpen}
        onOpenChange={setIsInviteOpen}
      />
      <CollaboratorEditModal
        collaborator={editingCollaborator}
        open={!!editingCollaborator}
        onOpenChange={(open) => {
          if (!open) setEditingCollaborator(null);
        }}
      />
    </div>
  );
}
